import React, { FC, useRef, useState, useEffect } from "react";
import { Table, Spin, Button } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import { useWorkplanQuery } from "~/graphql/query";
import { formatTime, WorkplanLineItem, WorkplanResourceAndTimeTotal } from "../../data-types";
import { useRestApi } from "~/hooks/use-rest-api";
import styles from "./workplan-table.module.css";

const {
  ["workplan-table"]: workplanTableClassName,
  ["workplan-header"]: workplanHeaderClassName,
  ["workplan-column-title"]: workplanColumnTitleClassName,
  ["workplan-column-value"]: workplanColumnValueClassName,
  ["workplan-cell-title"]: workplanCellTitleClassName,
  ["workplan-cell-value"]: workplanCellValueClassName,
  ["workplan-table-row"]: workplanTableRowClassName,
  ["workplan-totals-row"]: workplanTotalsRowClassName,
  ["scrollable-hover"]: scrollableHoverClassName,
} = styles as any;

type Props = {
  matterId: number;
  timelineActivationKey: number;
};

interface TableRow {
  [index: string]: string | number;
}

const firstColumn = [
  {
    title: "",
    dataIndex: "work",
    key: "work",
  },
];

const calculateLineItemRows = (rowsData: WorkplanLineItem[], colKeys: TableRow): TableRow[] => {
  const retData = [];

  for (let i = 0; i < rowsData.length; i++) {
    retData.push(calculateLineItemRow(rowsData[i], colKeys));
  }

  return retData;
};

const calculateLineItemRow = (rowData: WorkplanLineItem, colKeys: TableRow): TableRow => {
  const { title, id: workItemId, resourceAndTime } = rowData;

  const dataRow: TableRow = {
    key: workItemId,
    work: title,
    ...colKeys,
  };

  for (let i = 0; i < resourceAndTime.length; i++) {
    const rt = resourceAndTime[i];
    const { rateId, timeRange = "" } = rt;
    dataRow[rateId] = timeRange
      ? formatTime(timeRange, {
          lowerUpperSeperator: " - ",
          hoursSuffix: "hrs",
          minutesSuffix: "mins",
          hoursMinutesSeperator: " ",
        })
      : "";
  }

  return dataRow;
};

const calculateTotalsRow = (
  totalsData: WorkplanResourceAndTimeTotal[],
  colKeys: TableRow
): TableRow => {
  const totalsRow: TableRow = {
    key: "totals",
    work: "TOTAL",
    ...colKeys,
  };

  for (let i = 0; i < totalsData.length; i++) {
    const tItem = totalsData[i];
    const { rateId, timeRange = "" } = tItem;
    totalsRow[rateId] = timeRange
      ? formatTime(timeRange, {
          lowerUpperSeperator: " - ",
          hoursSuffix: "hrs",
          minutesSuffix: "mins",
          hoursMinutesSeperator: " ",
        })
      : "";
  }

  return totalsRow;
};

export const WorkplanTable: FC<Props> = ({ matterId, timelineActivationKey }) => {
  const { loading, data, refetch } = useWorkplanQuery(matterId);
  const workplan = data?.generateWorkplan;
  const downloadRef = useRef<HTMLAnchorElement>(null);
  const [downloading, setDownloading] = useState(false);
  const restApi = useRestApi();

  useEffect(() => {
    if (refetch) {
      refetch();
    }
  }, [timelineActivationKey, refetch]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const fileDownloader = await restApi.getWorkplanSpreadsheet(matterId);
      if (downloadRef.current) {
        await fileDownloader.download(downloadRef.current);
      }
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div
        style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}
      >
        <Spin size="large" tip="Loading workplan..." />
      </div>
    );
  }

  if (!workplan) {
    return (
      <div
        style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}
      >
        No workplan data available
      </div>
    );
  }

  const { rates, lineItems, resourceAndTimeTotals } = workplan;

  const colKeys: TableRow = {};

  // Merge first column (work) with rates
  const columns = [
    {
      ...firstColumn[0],
      fixed: "left" as const,
      width: 250,
      ellipsis: false,
      className: workplanColumnTitleClassName,
      title: () => (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span>Title</span>
        </div>
      ),
      render: (text: string) => <div className={workplanCellTitleClassName}>{text}</div>,
    },
    ...rates.map((rate) => {
      const { id, feeEarner, feeEarnerLevel } = rate;

      const title =
        feeEarner?.name && feeEarner?.name !== ""
          ? `${feeEarner?.name} (${feeEarner.feeEarnerLevel?.description}) `
          : `${feeEarnerLevel?.description}`;

      (colKeys as any)[id] = "";

      return {
        title,
        dataIndex: id,
        key: id,
        width: 200,
        ellipsis: false,
        className: workplanColumnValueClassName,
        align: "right" as const,
        render: (text: string) => <div className={workplanCellValueClassName}>{text}</div>,
      };
    }),
  ];

  // Set rows for work + time, but remove the totals row since we use summary
  const rows = calculateLineItemRows(lineItems, colKeys);

  const renderSummary = () => {
    return (
      <Table.Summary fixed>
        <Table.Summary.Row>
          <Table.Summary.Cell index={0}>
            <strong>Total</strong>
          </Table.Summary.Cell>
          {resourceAndTimeTotals.map((total, idx) => (
            <Table.Summary.Cell key={total.rateId} index={idx + 1} align="right">
              <strong>
                {formatTime(total.timeRange, {
                  lowerUpperSeperator: " - ",
                  hoursSuffix: "hrs",
                  minutesSuffix: "mins",
                  hoursMinutesSeperator: " ",
                })}
              </strong>
            </Table.Summary.Cell>
          ))}
        </Table.Summary.Row>
      </Table.Summary>
    );
  };

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <div className={workplanHeaderClassName}>
        <h3>Workplan</h3>
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          loading={downloading}
          onClick={handleDownload}
        >
          Download Excel
        </Button>
      </div>
      <div
        className={workplanTableClassName}
        style={{ paddingTop: "16px", paddingBottom: "16px", flex: 1, overflow: "hidden" }}
      >
        <Table
          dataSource={rows}
          columns={columns}
          pagination={false}
          scroll={{ x: Math.max(rates.length * 200 + 250, 800), y: "calc(65vh - 120px)" }}
          sticky
          rowClassName={(record) =>
            record.key === "totals" ? workplanTotalsRowClassName : workplanTableRowClassName
          }
          size="middle"
          bordered
          className={scrollableHoverClassName}
          summary={renderSummary}
          tableLayout="fixed"
        />
      </div>
      <a ref={downloadRef} style={{ display: "none" }} />
    </div>
  );
};
